/**
 * Wishlist Manager - Moon Moon Agency
 * Connecte l'UI du theme (boutons + compteur header + page wishlist)
 * a l'API JS de l'app Wishlist Power (window.Maestrooo.wishlist).
 *
 * API app :
 *   window.Maestrooo.wishlist.addProduct(handle, variant_id)
 *   window.Maestrooo.wishlist.removeProduct(handle, variant_id)
 *   window.Maestrooo.wishlist.containsProduct(handle, variant_id, product_id) -> Promise<boolean>
 *   window.Maestrooo.wishlist.getContent() -> Promise<{ products: [...] }>
 * Events : 'ooo-wishlist:product-added' / 'ooo-wishlist:product-removed'
 *   detail = { product, wishlist_product_list, wishlist_products_count }
 */

/*********************************************************************************************
 * 🤍 WISHLIST MANAGER
 */
const WishlistManager = {
	config: {
		selectors: {
			button: '[data-wishlist-button]',
			count: '[data-wishlist-count]',
			page: '[data-wishlist-page]',
			grid: '[data-wishlist-grid]',
			loading: '[data-wishlist-loading]',
			empty: '[data-wishlist-empty]',
			removeItem: '[data-wishlist-remove]',
			item: '[data-wishlist-item]',
		},
		classes: {
			active: 'mm-active',
		},
		// Vue Liquid servie pour rendre une carte wishlist isolee (template product.wishlist-card.liquid)
		cardView: 'wishlist-card',
	},

	init() {
		this._bindEvents();
		this._syncCountFromApi();
		this._initButtonsState();

		const page = window.getElement(this.config.selectors.page);
		if (page) this._renderPage(page);

		window.log('info', '🤍 [WISHLIST]', 'Initialized');
	},

	/*****************************************************************************
	 * EVENTS
	 */
	_bindEvents() {
		// Toggle d'un produit (carte ou page produit)
		document.addEventListener('click', (e) => {
			const button = e.target.closest(this.config.selectors.button);
			if (button) {
				e.preventDefault();
				this._toggleProduct(button);
				return;
			}

			// Suppression depuis la page wishlist
			const removeBtn = e.target.closest(this.config.selectors.removeItem);
			if (removeBtn) {
				e.preventDefault();
				this._removeFromPage(removeBtn);
			}
		});

		// Sync sur les events de l'app (depuis n'importe quelle source)
		document.addEventListener('ooo-wishlist:product-added', (e) => this._onWishlistChange(e));
		document.addEventListener('ooo-wishlist:product-removed', (e) => this._onWishlistChange(e));
	},

	_onWishlistChange(event) {
		const detail = event.detail || {};
		if (typeof detail.wishlist_products_count === 'number') {
			this._updateCount(detail.wishlist_products_count);
		} else {
			this._syncCountFromApi();
		}

		// Re-synchronise l'etat de tous les boutons d'un meme handle present sur la page
		const handle = detail.product && detail.product.handle;
		if (handle) this._refreshButtonsForHandle(handle);
	},

	/*****************************************************************************
	 * TOGGLE PRODUIT
	 */
	_toggleProduct(button) {
		const api = this._api();
		if (!api) {
			window.log('warn', '🤍 [WISHLIST]', 'API Wishlist Power indisponible');
			return;
		}

		const handle = button.dataset.handle;
		if (!handle) return;
		const variantId = button.dataset.variantId ? Number(button.dataset.variantId) : null;
		const isActive = button.getAttribute('aria-pressed') === 'true';

		if (isActive) {
			api.removeProduct(handle, variantId);
			this._setButtonState(button, false);
		} else {
			api.addProduct(handle, variantId);
			this._setButtonState(button, true);
		}
	},

	_setButtonState(button, active) {
		button.setAttribute('aria-pressed', String(active));
		button.classList.toggle(this.config.classes.active, active);
		const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
		if (label) button.setAttribute('aria-label', label);
	},

	_refreshButtonsForHandle(handle) {
		const buttons = window.getElements(this.config.selectors.button);
		buttons.forEach((button) => {
			if (button.dataset.handle !== handle) return;
			this._setButtonStateFromApi(button);
		});
	},

	_setButtonStateFromApi(button) {
		const api = this._api();
		if (!api || !button.dataset.handle) return;
		// 3e argument = product id : sans lui, l'app fetch /products/<handle>.js pour
		// le resoudre -> 1 requete par carte au chargement d'une collection.
		api.containsProduct(button.dataset.handle, null, button.dataset.productId)
			.then((isIn) => this._setButtonState(button, Boolean(isIn)))
			.catch(() => {});
	},

	_initButtonsState() {
		const buttons = window.getElements(this.config.selectors.button);
		if (!buttons.length) return;
		this._whenApiReady(() => {
			buttons.forEach((button) => this._setButtonStateFromApi(button));
		});
	},

	/*****************************************************************************
	 * COMPTEUR HEADER
	 */
	_updateCount(count) {
		const badges = window.getElements(this.config.selectors.count);
		badges.forEach((badge) => {
			badge.textContent = count;
			if (count > 0) {
				badge.removeAttribute('hidden');
			} else {
				badge.setAttribute('hidden', '');
			}
		});
	},

	_syncCountFromApi() {
		this._whenApiReady(() => {
			this._api().getContent()
				.then((result) => {
					const products = (result && result.products) || [];
					this._updateCount(products.length);
				})
				.catch((error) => window.log('error', '🤍 [WISHLIST]', 'getContent failed', { error: error.message }));
		});
	},

	/*****************************************************************************
	 * PAGE WISHLIST
	 */
	_renderPage(page) {
		const grid = page.querySelector(this.config.selectors.grid);
		const loading = page.querySelector(this.config.selectors.loading);
		const empty = page.querySelector(this.config.selectors.empty);
		if (!grid) return;

		this._whenApiReady(() => {
			this._api().getContent()
				.then((result) => {
					const products = (result && result.products) || [];
					if (loading) loading.setAttribute('hidden', '');

					if (!products.length) {
						if (empty) empty.removeAttribute('hidden');
						return;
					}

					return this._fetchCards(products).then((cards) => {
						grid.replaceChildren(...cards);
						grid.removeAttribute('hidden');
						// Les cartes sont injectees apres l'init du cart manager :
						// (re)attache le handler AJAX d'ajout panier aux nouveaux forms.
						if (typeof window.initAddToCartForms === 'function') {
							window.initAddToCartForms();
						}
					});
				})
				.catch((error) => {
					if (loading) loading.setAttribute('hidden', '');
					if (empty) empty.removeAttribute('hidden');
					window.log('error', '🤍 [WISHLIST]', 'Render page failed', { error: error.message });
				});
		});
	},

	// Recupere chaque carte et n'extrait que le node .mm-wishlist-card
	// (le fragment ?view= renvoie un document complet avec <head>)
	_fetchCards(products) {
		const view = this.config.cardView;
		const requests = products.map((product) => {
			const handle = product.handle;
			if (!handle) return Promise.resolve(null);
			const url = `${this._rootPath()}products/${handle}?view=${view}`;
			return fetch(url)
				.then((response) => (response.ok ? response.text() : ''))
				.then((html) => {
					if (!html) return null;
					const node = new DOMParser()
						.parseFromString(html, 'text/html')
						.querySelector(this.config.selectors.item);
					// L'app indexe ses entrees par (product_id, variant_id) : on conserve le
					// variant retourne par getContent() pour pouvoir cibler la bonne ligne
					// au moment de la suppression (un removeProduct(handle, null) ne matche
					// aucune entree ajoutee avec un variant).
					if (node && product.variant_id) {
						node.dataset.variantId = product.variant_id;
					}
					return node;
				})
				.catch(() => null);
		});
		return Promise.all(requests).then((nodes) => nodes.filter(Boolean));
	},

	_removeFromPage(removeBtn) {
		const api = this._api();
		const handle = removeBtn.dataset.handle;
		if (!api || !handle) return;

		const item = removeBtn.closest(this.config.selectors.item);
		// Le variant est porte par la carte (pose par _fetchCards depuis getContent)
		const variantId = item && item.dataset.variantId ? Number(item.dataset.variantId) : null;

		// Evite les clics repetes pendant l'appel reseau
		removeBtn.disabled = true;

		// On attend la confirmation de l'app avant de retirer la carte : un retrait
		// optimiste masquerait un echec et la carte reapparaitrait au refresh.
		Promise.resolve(api.removeProduct(handle, variantId))
			.then(() => api.containsProduct(handle))
			.then((stillIn) => {
				if (stillIn) {
					removeBtn.disabled = false;
					window.log('error', '🤍 [WISHLIST]', 'Suppression refusee par l\'app', { handle, variantId });
					return;
				}
				if (item) item.remove();
				this._refreshEmptyState();
			})
			.catch((error) => {
				removeBtn.disabled = false;
				window.log('error', '🤍 [WISHLIST]', 'Suppression echouee', { handle, error: error.message });
			});
	},

	// Bascule sur l'etat vide quand la grille n'a plus aucune carte
	_refreshEmptyState() {
		const page = window.getElement(this.config.selectors.page);
		if (!page) return;
		if (page.querySelectorAll(this.config.selectors.item).length > 0) return;

		const grid = page.querySelector(this.config.selectors.grid);
		const empty = page.querySelector(this.config.selectors.empty);
		if (grid) grid.setAttribute('hidden', '');
		if (empty) empty.removeAttribute('hidden');
	},

	/*****************************************************************************
	 * HELPERS
	 */
	_api() {
		return (window.Maestrooo && window.Maestrooo.wishlist) || null;
	},

	// Attend que l'API de l'app soit prete (chargee de maniere async par l'app)
	_whenApiReady(callback, attempt) {
		attempt = attempt || 0;
		if (this._api()) {
			callback();
			return;
		}
		if (attempt > 40) {
			window.log('warn', '🤍 [WISHLIST]', 'API Wishlist Power introuvable apres 10s');
			return;
		}
		setTimeout(() => this._whenApiReady(callback, attempt + 1), 250);
	},

	_rootPath() {
		// Conserve le prefixe de langue (ex: /fr/) pour les fetchs localises
		const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		return root.endsWith('/') ? root : `${root}/`;
	},
};

// Auto-init avec verification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => WishlistManager.init());
} else {
	WishlistManager.init();
}